import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setDone(true);
    } catch (err) {
      // Backend always returns success to avoid email enumeration; show same UX.
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8f9ff] p-4" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 border border-[#c3c6d7]/30" data-testid="forgot-password-page">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#434655] hover:text-[#004ac6] mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
        </Link>

        {!done ? (
          <>
            <h1 className="text-2xl font-extrabold text-[#0b1c30] tracking-tight mb-1">Forgot password?</h1>
            <p className="text-sm text-[#434655] mb-6">Enter the email associated with your Sovereign IQ account and we'll send a one-time reset link.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#434655] font-semibold mb-1 block">Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#c3c6d7]" />
                  <input
                    type="email" required
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    data-testid="forgot-email-input"
                    className="w-full bg-[#f8f9ff] border border-[#c3c6d7]/40 rounded-lg pl-10 pr-4 py-2.5 text-sm text-[#0b1c30] focus:ring-1 focus:ring-[#004ac6] outline-none"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="forgot-submit-btn"
                className="w-full bg-gradient-to-r from-[#004ac6] to-[#7c3aed] text-white py-3 rounded-lg text-sm font-bold hover:shadow-lg transition-all disabled:opacity-50">
                {submitting ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
          </>
        ) : (
          <div className="text-center py-4" data-testid="forgot-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <h1 className="text-xl font-extrabold text-[#0b1c30] tracking-tight mb-2">Check your inbox</h1>
            <p className="text-sm text-[#434655] mb-4">
              If an account exists for <strong>{email}</strong>, we've sent a one-time reset link.
              The link is valid for one hour.
            </p>
            <p className="text-[11px] text-[#737686]">Don't see it? Check spam, or try again with a different address.</p>
          </div>
        )}
      </div>
    </div>
  );
}
